import { getChainNameById } from '@/utils/network/getChainNameById'
import { getNetworkName } from '@/utils/network/getNetworkName'
import { useMemo } from 'react'
import type { Chain } from 'viem'
import { useChains } from 'wagmi'
import { Button } from '../common/button'
import { NetworkImage } from '../common/networkImage'
import { Popover, PopoverContent, PopoverList, PopoverTrigger } from '../common/popover'
import { ScrollArea, ScrollBar } from '../common/scrollArea'
import { Separator } from '../common/separator'
import { ArrowIcon } from '../icons/arrow'
import { CheckIcon } from '../icons/check'

interface Props {
  modal?: boolean
  chain: Chain
  onSelect: (chain: Chain) => void
}

export const NetworkSelect = ({ modal, chain, onSelect }: Props) => {
  const allChains = useChains()

  const chains = useMemo(() => {
    return [...allChains].sort((a, b) =>
      getNetworkName(a.name.toLowerCase()).localeCompare(getNetworkName(b.name.toLowerCase()))
    )
  }, [allChains])

  return (
    <Popover modal={modal}>
      <PopoverTrigger asChild>
        <Button
          variant="default"
          size="md"
          className="group flex gap-2 rounded-xl border hocus:border-accent bg-page px-3 text-left text-base text-body-foreground transition-colors"
        >
          <div className="flex w-full items-center gap-2">
            <NetworkImage network={getChainNameById(chain.id)} className="size-6" />
            <span className="capitalize">{getNetworkName(getChainNameById(chain.id))}</span>
          </div>
          <div className="group-data-[state=open]:-scale-y-100 transition-transform">
            <ArrowIcon direction="down" size={24} />
          </div>
        </Button>
      </PopoverTrigger>
      <PopoverContent>
        <ScrollArea className="flex max-h-36 flex-col overflow-y-auto">
          <PopoverList>
            {chains.map((chainOption: Chain) => (
              <li key={chainOption.id} className="group">
                <Button
                  size="default"
                  variant="default"
                  className="w-full justify-between px-3 py-2 text-base"
                  onClick={() => onSelect(chainOption)}
                >
                  <div className="flex items-center gap-2">
                    <NetworkImage network={getChainNameById(chainOption.id)} className="size-6" />
                    <span className="capitalize">{getNetworkName(getChainNameById(chainOption.id))}</span>
                  </div>
                  {chain.id === chainOption.id && <CheckIcon size={16} className="text-success transition-colors" />}
                </Button>
                <Separator className="group-last:hidden" />
              </li>
            ))}
          </PopoverList>
          <ScrollBar />
        </ScrollArea>
      </PopoverContent>
    </Popover>
  )
}
